import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { IonCheckbox, IonCol, IonItem, IonLabel, IonRow, IonSegment, IonSegmentButton } from "@ionic/react";
import { Event, EventEntry } from "../../models";
import { getEventEntriesByEventIdPersonId, getEventEntriesByPersonId } from "../../utilities/eventEntry/EventEntry";
import { EventEntryStatusOptions } from "../../utilities/eventEntry/EventEntryStatus";
import { sortEventEntriesByCreatedDate } from "../../utilities/eventEntry/SortEventEntry";
import { getEventsByOrganizationId } from "../../utilities/events/Event";
import { sortEventsByDate } from "../../utilities/events/SortEvents";
import SelectEvent from "../Event/SelectEvent";
import BasicEntryInfoCard from "../Entry/BasicEntryInfoCard";
import BasicModal from "./BasicModal";

type EntrySourceType = "current" | "previous";

interface CopyPreviousEntryModalProps {
    isOpen: boolean;
    onClose: () => void;
    currentEvent: Event;
    currentEventEntries: EventEntry[];
    personId: string;
    onContinue?: (entries: EventEntry[]) => void | Promise<void>;
}

const isCompletedEntry = (entry: EventEntry): boolean => entry.status === EventEntryStatusOptions.complete;

const isSubmittedEntry = (entry: EventEntry): boolean =>
    entry.status === EventEntryStatusOptions.complete || entry.status === EventEntryStatusOptions.submitted;

/** True when the user has at least one entry they can copy from (current completed or prior submitted). */
export async function userHasCopyableEntries(currentEventId: string, personId: string, currentEventEntries: EventEntry[]): Promise<boolean> {
    if (currentEventEntries.some(isCompletedEntry)) {
        return true;
    }

    const queryResult = await getEventEntriesByPersonId(personId);
    if (!queryResult.isSuccess) {
        return false;
    }

    return (queryResult.result ?? []).some((entry: EventEntry) => entry.eventId !== currentEventId && isSubmittedEntry(entry));
}

const CopyPreviousEntryModal: React.FC<CopyPreviousEntryModalProps> = ({
    isOpen,
    onClose,
    currentEvent,
    currentEventEntries,
    personId,
    onContinue,
}) => {
    const [entrySource, setEntrySource] = useState<EntrySourceType>("current");
    const [previousEvents, setPreviousEvents] = useState<Event[]>([]);
    const [selectedPreviousEvent, setSelectedPreviousEvent] = useState<Event | undefined>();
    const [previousEventEntries, setPreviousEventEntries] = useState<EventEntry[]>([]);
    const [selectedEntryIds, setSelectedEntryIds] = useState<string[]>([]);
    const [isLoadingPreviousEntries, setIsLoadingPreviousEntries] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");
    /** Prevents checkbox/row toggles while copying or after the modal begins closing. */
    const isInteractionLockedRef = useRef(false);

    const completedCurrentEntries = useMemo(
        () => sortEventEntriesByCreatedDate(currentEventEntries.filter(isCompletedEntry)) ?? [],
        [currentEventEntries]
    );

    const loadPreviousEvents = useCallback(async () => {
        if (!currentEvent.organizationId) {
            setPreviousEvents([]);
            return;
        }

        const eventsResult = await getEventsByOrganizationId(currentEvent.organizationId);
        if (!eventsResult.isSuccess) {
            setErrorMessage("Could not load previous horse shows.");
            setPreviousEvents([]);
            return;
        }

        const otherEvents = (eventsResult.result ?? []).filter((event: Event) => event.id !== currentEvent.id);
        setPreviousEvents(sortEventsByDate(otherEvents) ?? otherEvents);
    }, [currentEvent.id, currentEvent.organizationId]);

    const loadPreviousEventEntries = useCallback(
        async (event: Event) => {
            setIsLoadingPreviousEntries(true);
            setErrorMessage("");
            const entriesResult = await getEventEntriesByEventIdPersonId(event.id, personId);
            if (!entriesResult.isSuccess) {
                setErrorMessage("Could not load entries for the selected show.");
                setPreviousEventEntries([]);
                setIsLoadingPreviousEntries(false);
                return;
            }

            const submittedEntries = (entriesResult.result ?? []).filter(isSubmittedEntry);
            setPreviousEventEntries(sortEventEntriesByCreatedDate(submittedEntries) ?? submittedEntries);
            setIsLoadingPreviousEntries(false);
        },
        [personId]
    );

    useEffect(() => {
        if (!isOpen) {
            isInteractionLockedRef.current = true;
            setEntrySource("current");
            setPreviousEvents([]);
            setSelectedPreviousEvent(undefined);
            setPreviousEventEntries([]);
            setSelectedEntryIds([]);
            setErrorMessage("");
            setIsLoadingPreviousEntries(false);
            return;
        }

        isInteractionLockedRef.current = false;
        setEntrySource(completedCurrentEntries.length > 0 ? "current" : "previous");
        void loadPreviousEvents();
    }, [isOpen, completedCurrentEntries.length, loadPreviousEvents]);

    useEffect(() => {
        if (!isOpen || entrySource !== "previous" || !selectedPreviousEvent) {
            return;
        }
        void loadPreviousEventEntries(selectedPreviousEvent);
    }, [isOpen, entrySource, selectedPreviousEvent, loadPreviousEventEntries]);

    const selectableEntries = entrySource === "current" ? completedCurrentEntries : previousEventEntries;

    useEffect(() => {
        setSelectedEntryIds([]);
    }, [entrySource, selectedPreviousEvent?.id]);

    const selectedEntries = selectableEntries.filter((entry) => entry.id && selectedEntryIds.includes(entry.id));

    const toggleEntrySelection = useCallback(
        (entryId: string | undefined) => {
            if (!entryId || isInteractionLockedRef.current || isLoadingPreviousEntries) return;

            setSelectedEntryIds((prev) =>
                prev.includes(entryId) ? prev.filter((id) => id !== entryId) : [...prev, entryId]
            );
        },
        [isLoadingPreviousEntries]
    );

    const handleContinue = () => {
        if (selectedEntries.length === 0 || isInteractionLockedRef.current) return;

        const entriesToCopy = selectedEntries;
        isInteractionLockedRef.current = true;
        onClose();
        void onContinue?.(entriesToCopy);
    };

    const currentShowLabel = currentEvent.nickname || currentEvent.name || "This show";

    return (
        <BasicModal
            isOpen={isOpen}
            onClose={onClose}
            title="Select A Previous Entry"
            isLoading={isLoadingPreviousEntries}
            errorMessage={errorMessage}
            footerButtons={[
                {
                    text: "Cancel",
                    color: "light",
                    onClick: onClose,
                },
                {
                    text: "Continue",
                    color: "success",
                    disabled: selectedEntryIds.length === 0 || isLoadingPreviousEntries,
                    onClick: handleContinue,
                },
            ]}>
            <>
                <IonRow className="ion-justify-content-center mb-0">
                    <IonCol className="ion-text-center pb-0">
                        <h2 className="text-info font-weight-bold mb-0">Where should we copy from?</h2>
                    </IonCol>
                </IonRow>
                <IonRow className="ion-justify-content-center mt-0 mb-3">
                    <IonCol sizeXs="12" sizeMd="10" className="ion-text-center pt-0">
                        <IonSegment
                            value={entrySource}
                            onIonChange={(event) => setEntrySource((event.detail.value as EntrySourceType) || "current")}>
                            <IonSegmentButton value="current">
                                <IonLabel>This Show</IonLabel>
                            </IonSegmentButton>
                            <IonSegmentButton value="previous">
                                <IonLabel>Previous Show</IonLabel>
                            </IonSegmentButton>
                        </IonSegment>
                    </IonCol>
                </IonRow>

                {entrySource === "current" ? (
                    <>
                        <IonRow className="ion-justify-content-center mt-0">
                            <IonCol className="ion-text-center pt-0">
                                <h5 className="text-primary font-weight-bold mt-0">
                                    Completed entries from {currentShowLabel}. Pick one or more to duplicate and edit
                                    only what needs to change.
                                </h5>
                            </IonCol>
                        </IonRow>
                        <IonRow className="ion-justify-content-center">
                            <IonCol sizeXs="12" sizeMd="10">
                                {completedCurrentEntries.length === 0 ? (
                                    <p className="ion-text-center text-muted mb-0">
                                        No completed entries on this show yet. Try a previous show, or complete an entry
                                        here first.
                                    </p>
                                ) : (
                                    isOpen && (
                                        <EntryCheckboxList
                                            entries={completedCurrentEntries}
                                            selectedEntryIds={selectedEntryIds}
                                            onToggle={toggleEntrySelection}
                                        />
                                    )
                                )}
                            </IonCol>
                        </IonRow>
                    </>
                ) : (
                    <>
                        <IonRow className="ion-justify-content-center mt-0">
                            <IonCol className="ion-text-center pt-0">
                                <h5 className="text-primary font-weight-bold mt-0">
                                    Choose a previous horse show, then select one or more completed entries to copy.
                                </h5>
                            </IonCol>
                        </IonRow>
                        <IonRow className="ion-justify-content-center">
                            <IonCol sizeXs="12" sizeMd="10">
                                {previousEvents.length === 0 ? (
                                    <p className="ion-text-center text-muted mb-0">
                                        No other horse shows found with entries from your account.
                                    </p>
                                ) : (
                                    <SelectEvent
                                        menuPlacement="bottom"
                                        selectedValue={selectedPreviousEvent?.id || null}
                                        events={previousEvents}
                                        onSelect={(event?: Event) => {
                                            setSelectedPreviousEvent(event);
                                            setSelectedEntryIds([]);
                                        }}
                                    />
                                )}
                            </IonCol>
                        </IonRow>
                        {selectedPreviousEvent && !isLoadingPreviousEntries && (
                            <IonRow className="ion-justify-content-center mt-3">
                                <IonCol sizeXs="12" sizeMd="10">
                                    {previousEventEntries.length === 0 ? (
                                        <p className="ion-text-center text-muted mb-0">
                                            No completed entries found for this show.
                                        </p>
                                    ) : (
                                        isOpen && (
                                            <EntryCheckboxList
                                                entries={previousEventEntries}
                                                selectedEntryIds={selectedEntryIds}
                                                onToggle={toggleEntrySelection}
                                            />
                                        )
                                    )}
                                </IonCol>
                            </IonRow>
                        )}
                    </>
                )}
            </>
        </BasicModal>
    );
};

interface EntryCheckboxListProps {
    entries: EventEntry[];
    selectedEntryIds: string[];
    onToggle: (entryId: string | undefined) => void;
}

const EntryCheckboxList: React.FC<EntryCheckboxListProps> = ({ entries, selectedEntryIds, onToggle }) => (
    <>
        {entries.map((entry, index) => {
            const entryId = entry.id || undefined;
            const isChecked = !!entryId && selectedEntryIds.includes(entryId);

            return (
                <IonItem
                    key={entry.id || index}
                    lines="none"
                    className="ion-no-padding mb-2"
                    button
                    detail={false}
                    onClick={() => onToggle(entryId)}
                    style={{
                        "--background": "transparent",
                        "--inner-padding-end": "0",
                        "--padding-start": "0",
                    }}>
                    <IonCheckbox
                        slot="start"
                        checked={isChecked}
                        color="success"
                        className="mr-2"
                        aria-hidden="true"
                        tabIndex={-1}
                        style={{ pointerEvents: "none" }}
                    />
                    <div style={{ flex: 1, minWidth: 0 }}>
                        <BasicEntryInfoCard currentEventEntry={entry} entryNumber={index + 1} />
                    </div>
                </IonItem>
            );
        })}
    </>
);

export default CopyPreviousEntryModal;
